/** Chemin API des logos « lettre » (fichiers souvent absents du front). */
const LETTER_LOGO_PATH_RE: RegExp = /\/images\/logos\/lettres\/([^/]+)\.svg$/i

/**
 * Décode une URL logo si elle contient des caractères encodés (`%C3%89` → `É`).
 * @param {string} raw Segment ou URL brute.
 * @returns {string} Valeur décodée, ou l'originale en cas d'échec.
 */
function decodeLogoPictSegment(raw: string): string {
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/**
 * Extrait la lettre affichable depuis le segment de fichier (`I`, `É`, etc.).
 * @param {string} fileSegment Partie entre `lettres/` et `.svg`.
 * @returns {string | null} Une lettre en majuscule, ou `null` si invalide.
 */
function parsePlaceholderLetterSegment(fileSegment: string): string | null {
  const letter: string = decodeLogoPictSegment(fileSegment).normalize('NFC').trim()
  if (!letter) return null

  const graphemes: string[] = [...letter]
  if (graphemes.length !== 1) return null
  if (!/\p{L}/u.test(graphemes?.[0] ?? '')) return null

  return graphemes?.[0]?.toLocaleUpperCase('fr-FR') ?? null
}

/**
 * Indique si l'URL est un placeholder lettre renvoyé par l'API.
 * @param {string | null | undefined} logoPict URL du logo.
 * @returns {boolean} `true` si le chemin correspond à `/images/logos/lettres/{lettre}.svg`.
 */
export function isLetterPlaceholderLogo(logoPict: string | null | undefined): boolean {
  const raw: string = logoPict?.trim() ?? ''
  if (!raw) return false

  const match: RegExpMatchArray | null = decodeLogoPictSegment(raw).match(LETTER_LOGO_PATH_RE)
  if (!match?.[1]) return false

  return parsePlaceholderLetterSegment(match[1]) !== null
}

/**
 * Extrait la lettre d'un placeholder logo API.
 * @param {string} logoPict URL du placeholder lettre.
 * @returns {string | null} La lettre en majuscule, ou `null` si l'URL ne correspond pas.
 */
export function getLetterFromPlaceholderLogo(logoPict: string): string | null {
  const match: RegExpMatchArray | null = decodeLogoPictSegment(logoPict.trim()).match(
    LETTER_LOGO_PATH_RE,
  )
  if (!match?.[1]) return null

  return parsePlaceholderLetterSegment(match[1])
}

/**
 * Retourne l'initiale à afficher pour une entité (école, programme…).
 * @param {string | null | undefined} logoPict URL du logo (placeholder lettre ou image réelle).
 * @param {string | null | undefined} entityName Nom affiché de l'entité.
 * @returns {string} Une lettre majuscule, ou `?` si le nom est vide.
 */
export function getEntityLogoInitial(
  logoPict: string | null | undefined,
  entityName: string | null | undefined,
): string {
  const fromUrl: string | null = logoPict ? getLetterFromPlaceholderLogo(logoPict) : null
  if (fromUrl) return fromUrl

  const trimmed: string = (entityName ?? '').trim()
  if (!trimmed) return '?'
  return trimmed.charAt(0).toLocaleUpperCase('fr-FR')
}

/**
 * URL d'image à utiliser, ou chaîne vide si placeholder lettre / absent.
 * @param {string | null | undefined} logoPict URL brute du logo.
 * @returns {string} URL affichable, ou `''` pour déclencher le fallback initiale.
 */
export function resolveEntityLogoSrc(logoPict: string | null | undefined): string {
  const raw: string = logoPict?.trim() ?? ''
  if (!raw || isLetterPlaceholderLogo(raw)) return ''
  return raw
}
